import { BaseFileFiledDto } from '@diritto/common/base/base.dto';
import {
  AgeRestriction,
  CategoryType,
  CategoryTypeId,
  ContentSerializeStatus,
  ContentStatus,
  MAX_CONTENT_KEYWORD_COUNT,
  MAX_CONTENT_KEYWORD_LENGTH,
  MAX_CONTENT_TITLE_LENGTH,
  ReplyAllow,
  UsageType,
} from '@diritto/common/content/constants';
import { ContentEditDto } from '@diritto/common/content/dtos/content.dto';
import { assert } from '@diritto/common/utils/assert';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { isCleanInput } from '@/utils/cleanInput';

export type EditContentData = {
  id?: number;
  title: string;
  description: string;
  category: CategoryType;
  ageRestriction: AgeRestriction;
  keywords: string[];
  cover?: BaseFileFiledDto;
  serializeStatus: ContentSerializeStatus;
  isFancyShareEnabled: UsageType;
  isHiddenContents?: boolean;
  reservedHideContentsAt?: Date;
  isMarketingAgreed?: boolean;
  isProposalWanted?: boolean;
  publishedLink: string;
  replyAllow: ReplyAllow;
  status: ContentStatus;
};

export type EditContestContentData = EditContentData & {
  contestId?: number;
};

export const convertToContestContentData = (content: ContentPublicRelayDto): EditContestContentData => ({
  id: content.id,
  title: content.title,
  description: content.description,
  category: content.categoryType || CategoryType.BL,
  ageRestriction: content.ageRestriction,
  keywords: content.keywords,
  isFancyShareEnabled: content.isFancyShareEnabled ? UsageType.Using : UsageType.NotUsing,
  isHiddenContents: content.isHiddenContents,
  isProposalWanted: content.isProposalWanted,
  reservedHideContentsAt: content.reservedHideContentsAt && new Date(content.reservedHideContentsAt),
  isMarketingAgreed: content.isMarketingAgreed,
  cover: content.cover,
  serializeStatus: content.serializeStatus,
  publishedLink: content.publishedLink,
  replyAllow: content.isCommentEnabled ? ReplyAllow.Allow : ReplyAllow.Deny,
  status: content.status,
  contestId: 3,
});

export const convertToContentData = (content: ContentPublicRelayDto): EditContentData => ({
  id: content.id,
  title: content.title,
  description: content.description,
  category: content.categoryType || CategoryType.BL,
  ageRestriction: content.ageRestriction,
  keywords: content.keywords,
  isFancyShareEnabled: content.isFancyShareEnabled ? UsageType.Using : UsageType.NotUsing,
  isHiddenContents: content.isHiddenContents,
  isProposalWanted: content.isProposalWanted,
  reservedHideContentsAt: content.reservedHideContentsAt && new Date(content.reservedHideContentsAt),
  isMarketingAgreed: content.isMarketingAgreed,
  cover: content.cover,
  serializeStatus: content.serializeStatus,
  publishedLink: content.publishedLink,
  replyAllow: content.isCommentEnabled ? ReplyAllow.Allow : ReplyAllow.Deny,
  status: content.status,
});

export const convertToEditContentDto = (content: EditContentData): ContentEditDto => {
  assert(content.cover?.id);
  return {
    title: content.title,
    description: content.description,
    categoryId: CategoryTypeId[content.category],
    coverImageId: content.cover?.id,
    isCommentEnabled: content.replyAllow === ReplyAllow.Allow,
    isFancyShareEnabled: content.isFancyShareEnabled === UsageType.Using,
    ageRestriction: content.ageRestriction,
    status: content.status,
    keywords: content.keywords,
    isHiddenContents: content.isHiddenContents ?? false,
    isProposalWanted: content.isProposalWanted ?? false,
    reservedHideContentsAt: content.reservedHideContentsAt,
    isMarketingAgreed: content.isMarketingAgreed ?? true,
    publishedLink: content.publishedLink,
    serializeStatus: content.serializeStatus,
  };
};

export const validateEditContent = (content: EditContentData) => {
  const { keywords } = content;
  const isAllUniqueKeyword = keywords.every((keyword, index) => index === keywords.findIndex(k => k === keyword));
  const isAllAllowKeywordLength = keywords.every(keyword => keyword.length <= MAX_CONTENT_KEYWORD_LENGTH);
  const isAllCleanKeyword = keywords.every(keyword => isCleanInput(keyword));

  if (!content.title) {
    return '제목을 입력해 주세요.';
  }
  if (content.title.length > MAX_CONTENT_TITLE_LENGTH) {
    return `제목은 ${MAX_CONTENT_TITLE_LENGTH}자까지만 입력할 수 있어요.`;
  }
  if (!isCleanInput(content.title)) {
    return '사용할 수 없는 문자가 포함되어 있어요.';
  }
  if (!content.description) {
    return '작품 설명을 입력해 주세요.';
  }
  if (!isCleanInput(content.description)) {
    return '사용할 수 없는 문자가 포함되어 있어요.';
  }
  if (!content.cover?.id) {
    return '표지를 등록해 주세요.';
  }
  if (!isAllUniqueKeyword) {
    return '키워드는 중복될 수 없어요.';
  }
  if (keywords.length > MAX_CONTENT_KEYWORD_COUNT) {
    return `키워드는 쉼표로 구분되며 최대 ${MAX_CONTENT_KEYWORD_COUNT}개만 입력할 수 있어요.`;
  }
  if (!isAllAllowKeywordLength) {
    return `키워드당 최대 ${MAX_CONTENT_KEYWORD_LENGTH}자 까지만 입력할 수 있어요.`;
  }
  if (!isAllCleanKeyword) {
    return '사용할 수 없는 문자가 포함되어 있어요.';
  }
  return undefined;
};
